import { generatePath } from 'react-router-dom';
import { Map as ImmutableMap } from 'immutable';

import { selector } from 'lib/selectors';
import {
  backSearchUrlSelector,
  cameFromToApproveCategorySelector,
  navigationSelector,
  userGUIDSelector,
} from 'domain/env';
import {
  currentCompanyIdSelector,
  documentDocumentIdSelector,
  documentsByIdSelector,
  documentTagsSelector,
  justPublishedDocumentSelector,
  isDocumentConfidentialSelector,
} from 'domain/documents/documentSelector';
import { isCurrentUserSignaturePendingSelector } from 'domain/approvals/selectors';
import CONST from 'domain/documents/constants';
import { queryToSearch } from 'domain/router/helpers';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import * as ACL from 'domain/restriction';

import { DecideNavigationInput } from './decideNavigation';

export interface WorkspaceBackLink {
  pathname: string;
  search: string;
}

// Mirrors `backLink` getter from `pages/document/index.jsx` (dev): if a
// `backSearchUrl` was captured at navigation time and includes a category,
// build the workspace URL from those params + query. Otherwise fall back
// to the company workspace without a category.
//
// This is the single source of truth for "where the user came from" and
// covers both the Well Done redirect (when `backSearchUrl.params.category1`
// is the "To approve" category) and the Inbox publish-last fallback.
// `cameFromToApproveCategorySelector` reads the same params via
// `navigationSelector`; both stores are updated synchronously by the
// workspace route, so they stay in sync.
export const workspaceBackLinkSelector = selector(
  backSearchUrlSelector,
  currentCompanyIdSelector,
  isDocumentConfidentialSelector,
  (
    backSearchUrl: { params?: { category1?: string }; query?: unknown } | null | undefined,
    companyId: string | undefined,
    isConfidential: boolean,
  ): WorkspaceBackLink | null => {
    if (backSearchUrl && backSearchUrl?.params?.category1) {
      const path = isConfidential
        ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute
        : ROUTES_PATH.COMPANY_WORKSPACE.absolute;

      return {
        pathname: generatePath(path, backSearchUrl.params),
        search: queryToSearch(backSearchUrl.query),
      };
    }

    if (!companyId) return null;

    const defaultPath = isConfidential
      ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.absolute
      : ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute;

    return { pathname: generatePath(defaultPath, { companyId }), search: '' };
  },
);

// Any accountant role (full BK, Limited BK, RO BK, Restricted BK).
// `IS_ACCOUNT` policy already covers all four — this is the gate that flips
// Inbox approve/reject from NEXT (client) to STAY (any BK).
export const isBkRoleUserSelector = selector(ACL.isGranted, (isGranted): boolean => isGranted(ACL.IS_ACCOUNT));

// "Publish flow is active" — true iff the user can drive the financial publish
// flow on the open document. Direct port of the legacy
// `useRedirectWhenApprovalsEnd` predicate
// `isGrantedWorkAsFinancial && !isLimitedBK && !isReadOnlyBK`:
//
//   - `IS_WORK_ON_WITH_CURRENT_DOCUMENT_AS_FINANCIAL` — current doc is
//     financial AND the user is BK-side (excludes regular / confidential /
//     restricted / confidential-restricted users; the policy is defined in
//     `domain/restriction.js`).
//   - Limited BK and RO BK are explicitly excluded — they don't have a
//     publish CTA on the financial form (see `isVisiblePublishButton` in
//     `pages/document/index.jsx`).
//
// Supplier user note: `IS_WORK_ON_WITH_CURRENT_DOCUMENT_AS_FINANCIAL`
// does not exclude supplier role at the policy level, so this selector
// returns true for a supplier on a financial doc — same as the legacy
// `useRedirectWhenApprovalsEnd` and `isGoNext` predicates. Practically
// irrelevant: suppliers don't see approval flows in production (separate
// portal). Kept as direct port; if a future change brings suppliers into
// approval, add `&& !isGranted(ACL.IS_SUPPLIER_USER)` here.
//
// Two roles inside `decideNavigation`:
//   - "To approve" (DA-19047): identifies the publishing BK whose approve/
//     publish navigation is state-based (advance only when the doc is approved
//     AND published), and gates the `allFinancialDocsPublishedOrRejected` clause
//     of the Well Done override.
//   - Inbox approve/reject staying gate: BK on financial → STAY, BK on general
//     → NEXT (DA-18889). Mirrors old `isGoNext` clause
//     `(!isAclFinancial || (isAclFinancial && status.includes('accepted')))`.
export const isPublishFlowActiveSelector = selector(
  ACL.isGranted,
  (isGranted): boolean =>
    isGranted(ACL.IS_WORK_ON_WITH_CURRENT_DOCUMENT_AS_FINANCIAL) &&
    !isGranted(ACL.IS_LIMITED_ACCOUNT) &&
    !isGranted(ACL.IS_READ_ONLY_ACCOUNTANT),
);

// True iff the current user has approved the open document — their signature is
// no longer pending. Synchronous & reliable: the approve saga dispatches
// `documentGet.success` (updating the approvals store) before the navigation
// decision runs, unlike publish which is async. Used by the "To approve"
// publishing-BK navigation gate in `decideNavigation`.
export const isCurrentDocumentApprovedByUserSelector = selector(
  isCurrentUserSignaturePendingSelector,
  (isSignaturePending: boolean): boolean => !isSignaturePending,
);

// True iff the open document is published — its backend system tags include
// `_S_ACCEPTED`. Read straight off `tags`, NOT the derived `status` array:
// `checkStatus`/`parseTags` build `status` from a small `StatusTags` map, so it
// is an unreliable proxy for the approval/publish lifecycle. Used by the
// "To approve" publishing-BK navigation gate.
//
// On production `acceptDocument` is synchronous: the accept saga dispatches
// `documentUpdate.success` before the decision runs, so a regular Publish's
// `_S_ACCEPTED` is already in the store here — no WS round-trip / saga re-run
// (that path is dev-only — out of scope for this hotfix). Quick publish stays
// async, handled by the optimistic-advance exception in `decideNavigation`.
export const isCurrentDocumentPublishedSelector = selector(
  documentTagsSelector,
  (tags): boolean => Boolean(tags?.includes(CONST.tags.isAccepted)),
);

// Personal approval queue size: number of documents in the local store that
// are pending approval AND list the current user as a next approver. Mirrors
// the legacy `getApproveDocumentsCount` selector that lived in
// `domain/documents/documentSelector.js`. Moved here because its only
// remaining consumer is the navigation decision logic.
export const personalApprovalQueueSizeSelector = selector(
  documentsByIdSelector,
  userGUIDSelector,
  (documentList, uID: string): number =>
    documentList.filter((d) => {
      const next = Array.isArray(d.approvals.next) ? d.approvals.next : [d.approvals.next];
      return d.tags.includes(CONST.tags.approvalPending) && next.includes(uID);
    }).size,
);

export const isPersonalApprovalQueueEmptySelector = selector(
  personalApprovalQueueSizeSelector,
  (size: number): boolean => size === 0,
);

// Publish process done for the whole list: every financial doc in the local
// navigation list is published (`_S_ACCEPTED`) OR approval-rejected
// (`_S_APPROVAL_REJECTED`), so none is awaiting publish. Publish-side completion
// signal — INDEPENDENT of the approval side (a doc can be published without
// being approved and vice versa). General docs are skipped: they don't go
// through the publish flow, so requiring `_S_ACCEPTED` would strand a BK on a
// list of general docs in "To approve" (DA-18888).
//
// Read off the backend system tags, NOT the derived `status` array. `status` is
// built by `checkStatus`/`parseTags` from a small `StatusTags` map that knows
// only `_S_REJECTED`/`_S_ACCEPTED` — it never encodes the approval-flow tags
// `_S_APPROVAL_COMPLETE`/`_S_APPROVAL_REJECTED`. A doc rejected in the approval
// flow carries `_S_APPROVAL_REJECTED` (NOT the document-level `_S_REJECTED`), so
// the old `status.includes('rejected')` check never matched it — and a rejected
// doc can still show `'accepted'` in `status`, making it useless here (DA-19047).
//
// Restored from the pre-DA-18892 `allFinancialDocsAcceptedSelector` with the
// rejected carve-out added. Paired with `isPersonalApprovalQueueEmptySelector`
// (the approval-side signal) for the Well Done override — for a publishing BK
// both independent signals are required.
//
// Caveat — quick publish is async ("schedule & continue"): it never flips
// `_S_ACCEPTED` synchronously. So the open doc also counts as publish-done if it
// was just published this session at all — `justPublished` is non-null: a
// reliable FE marker set by the publish/schedule sagas ON SUCCESS and reset to
// null on every doc open (pages/document/sagas), so it is scoped to the current
// doc. Sync publishes already carry `_S_ACCEPTED` above, so in practice this only
// adds the async quick-publish case. Lets a publishing BK reach Well Done after
// quick-publishing the last doc (or quick-publish then approve it) without waiting
// on the ERP result (DA-19047 / DA-18891). On `dev`, regular publish is async too
// (WebSocket rework) — this hotfix targets production, where accept is synchronous.
export const allFinancialDocsPublishedOrRejectedSelector = selector(
  documentsByIdSelector,
  documentDocumentIdSelector,
  justPublishedDocumentSelector,
  (docsById, currentDocId: string, justPublished): boolean => {
    if (docsById.size === 0) return false;
    const wasCurrentDocJustPublished = Boolean(justPublished);
    return docsById.every(
      (d) =>
        d.doctype !== CONST.doctype.financial ||
        d.tags?.includes(CONST.tags.isAccepted) ||
        d.tags?.includes(CONST.tags.approvalRejected) ||
        (wasCurrentDocJustPublished && d.documentID === currentDocId),
    );
  },
);

export type NavigationDecisionContext = Omit<DecideNavigationInput, 'action'>;

export const navigationDecisionInputSelector = selector(
  cameFromToApproveCategorySelector,
  navigationSelector,
  workspaceBackLinkSelector,
  isBkRoleUserSelector,
  isPublishFlowActiveSelector,
  isCurrentDocumentApprovedByUserSelector,
  isCurrentDocumentPublishedSelector,
  justPublishedDocumentSelector,
  allFinancialDocsPublishedOrRejectedSelector,
  isPersonalApprovalQueueEmptySelector,
  (
    isToApproveCategory: boolean,
    navigation: ImmutableMap<string, unknown>,
    backLink: WorkspaceBackLink | null,
    isBkRoleUser: boolean,
    isPublishFlowActive: boolean,
    isCurrentDocApproved: boolean,
    isCurrentDocPublished: boolean,
    justPublished: string | null,
    allFinancialDocsPublishedOrRejected: boolean,
    isPersonalApprovalQueueEmpty: boolean,
  ): NavigationDecisionContext => ({
    isToApproveCategory,
    isBkRoleUser,
    isPublishFlowActive,
    isCurrentDocApproved,
    isCurrentDocPublished,
    isCurrentDocJustPublished: Boolean(justPublished),
    hasNextDoc: ((navigation.get('listLength') as number | undefined) ?? 0) >= 2,
    hasBackLink: Boolean(backLink),
    allFinancialDocsPublishedOrRejected,
    isPersonalApprovalQueueEmpty,
  }),
);
